import React, { useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { useEffect } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SkeletonTable from "../Skeleton/TableSkeleton";
import { getSTOrderBookList } from "../../Actions/TradePage/STOrderBookAction";
import { useExchangeOrderBook } from "../../socket";
import useBinance24hTicker from "../../socket/BinanceTickerSocket";
import { a11yProps, CustomTabPanel } from "../common/CustomTabs";

const StyledTableCell = styled(TableCell)(() => ({
    padding: '0px !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#3665ff',
        color: '#fff',
        padding: '0px 5px !important', // ✅ 0 padding in <th>
        fontSize: 12,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        border: 'none',
        padding: '0px 5px !important',
    },
}));
const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(even)': {
        // backgroundColor: '#3665ff0d',
        border: 'none',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 'none',
    },
}));

function OrderBook({ layoutchange }) {

    // tab 
    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    const [orderSize, setOrderSize] = useState(0.1);




    // Hooks
    const location = useLocation();
    const dispatch = useDispatch();

    // Selectors
    // const { isAuthenticated } = useSelector((state) => state.auth);
    // const { userDetailData, isFirstLoad, userDetailStatus } = useSelector((state) => state.user);
    const { tradePairData, tradePairLoading } = useSelector(state => state.tradePair);

    // Query Params
    const queryParams = new URLSearchParams(location.search);
    const initialSymbol = queryParams.get("symbol");

    // State
    const [tradePairDetail, setTradePairDetail] = useState({});
    const [load, setLoad] = useState(true);
    const [bids, setBids] = useState([]);
    const [asks, setAsks] = useState([]);
    const [ratio, setRatio] = useState(null);
    const [loading, setLoading] = useState(true);

    // Custom Hooks
    const { data } = useBinance24hTicker(initialSymbol);

    /** =====================
     * Fetch trade pair detail
     * ===================== */

    useEffect(() => {
        if (!tradePairLoading) {
            let initialTradePair = {};
            if (initialSymbol) {
                initialTradePair = tradePairData?.data?.find((item) => item.symbol?.toUpperCase() === initialSymbol?.toUpperCase())
            } else {
                initialTradePair = tradePairData?.data?.[0] || {};
            }
            setTradePairDetail(initialTradePair);
            setLoad(false);
        };
    }, [tradePairLoading, initialSymbol]);

    /** =====================
     * Dispatch initial order book fetch (Redux)
     * ===================== */
    useEffect(() => {
        if (initialSymbol) dispatch(getSTOrderBookList({ symbol: initialSymbol?.toUpperCase() }));
    }, [initialSymbol]);

    const priceType = tradePairDetail?.priceType || "Binance"; // fallback

    const orderBookData = useExchangeOrderBook(tradePairDetail?.symbol, priceType);

    useEffect(() => {
        if (!orderBookData?.bids || !orderBookData?.asks) return;

        setLoading(false);

        const normalize = (o) => {
            if (o?.qty !== undefined && o?.postprice !== undefined) {
                return { price: Number(o.postprice), quantity: Number(o.qty) };
            }

            if (Array.isArray(o)) {
                return { price: Number(o[0]), quantity: Number(o[1]) };
            }

            return { price: Number(o.price), quantity: Number(o.quantity) };
        };

        const bids = orderBookData.bids
            .map(normalize)
            .sort((a, b) => b.price - a.price);

        const asks = orderBookData.asks
            .map(normalize)
            .sort((a, b) => a.price - b.price);

        // Calculate cumulative + total
        let bidSum = 0;
        const bidsWithCumulative = bids.map((r) => {
            bidSum += r.quantity;
            return {
                ...r,
                cumulative: bidSum,
                total: r.price * r.quantity, // ✅ Binance-style total
            };
        });

        let askSum = 0;
        const asksWithCumulative = asks.map((r) => {
            askSum += r.quantity;
            return {
                ...r,
                cumulative: askSum,
                total: r.price * r.quantity, // ✅ Binance-style total
            };
        });

        const maxDepth = Math.max(bidSum, askSum, 1);

        // Add percentage
        setBids(
            bidsWithCumulative.map((r) => ({
                ...r,
                percentage: (r.cumulative / maxDepth) * 100,
            }))
        );

        setAsks(
            asksWithCumulative.map((r) => ({
                ...r,
                percentage: (r.cumulative / maxDepth) * 100,
            }))
        );
    }, [orderBookData]);


    useEffect(() => {
        if (!data) return;

        //  Ignore if symbol mismatch
        if (data.symbol !== initialSymbol) return;

        setTradePairDetail((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                lastPrice: data.lastPrice,
                livePrice: data.lastPrice,
                change24h: data.change24h,
                changePercent24h: data.changePercent24h,
                baseVolume24h: data.volume24h,
                quoteVolume24h: data.quoteVolume24h,
                high24h: data.high24h,
                low24h: data.low24h,
            };
        });
    }, [data, initialSymbol]);


    useEffect(() => {
        setBids([]);
        setAsks([]);
        setRatio(null);
        setLoading(true);
    }, [priceType]);

    return (

        <div style={{ background: 'var(--color-BasicBg)', padding: '5px', height: '100%' }}>
            <Box sx={{ width: '100%' }}>
                <Box className="display-2 futures-trade-table">
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="none"
                        className="no-drag odr-book-tab"
                        sx={{
                            "& .MuiTab-root": {
                                minWidth: 40,
                                color: "#64748B",
                            },
                            "& .Mui-selected": {
                                color:
                                    value === 2
                                        ? "#0ECB81"
                                        : value === 1
                                            ? "#F6465D"
                                            : "#64748B",
                            },
                        }}
                    >

                        <Tab icon={<svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
                            <path d="M7.66802 0.643173H0.624023V6.51317H7.66802V0.643173Z" fill="#F25575"></path>
                            <path d="M7.66802 8.18994H0.624023V14.0599H7.66802V8.18994Z" fill="#0ECB81"></path>
                            <path d="M16.7247 0.643173H9.68066V3.99746H16.7247V0.643173Z" fill="#C7D2E0"></path>
                            <path d="M16.7247 5.67459H9.68066V9.02888H16.7247V5.67459Z" fill="#C7D2E0"></path>
                            <path d="M16.7247 10.7063H9.68066V14.0606H16.7247V10.7063Z" fill="#C7D2E0"></path>
                        </svg>} {...a11yProps(0)} />

                        <Tab
                            {...a11yProps(1)}
                            sx={{
                                "&.Mui-selected": { color: "#F6465D" }, // Binance red
                            }}
                            icon={
                                <svg width="17" height="15" viewBox="0 0 17 15">
                                    <path d="M7.14314 0.643311H0.43457V14.0604H7.14314V0.643311Z" fill="currentColor" />
                                    <path d="M16.5352 0.643311H9.49121V3.9976H16.5352V0.643311Z" fill="currentColor" />
                                    <path d="M16.5352 5.67473H9.49121V9.02901H16.5352V5.67473Z" fill="currentColor" />
                                    <path d="M16.5352 10.7064H9.49121V14.0607H16.5352V10.7064Z" fill="currentColor" />
                                </svg>
                            }
                        />

                        <Tab
                            {...a11yProps(2)}
                            sx={{
                                "&.Mui-selected": { color: "#F6465D" },
                            }}
                            icon={
                                <svg width="17" height="15" viewBox="0 0 17 15">
                                    <path d="M6.95466 0.643478H0.246094V14.0606H6.95466V0.643478Z" fill="currentColor" />
                                    <path d="M16.3467 0.643478H9.30273V3.99776H16.3467V0.643478Z" fill="currentColor" />
                                    <path d="M16.3467 5.6749H9.30273V9.02918H16.3467V5.6749Z" fill="currentColor" />
                                    <path d="M16.3467 10.7066H9.30273V14.0609H16.3467V10.7066Z" fill="currentColor" />
                                </svg>
                            }
                        />

                    </Tabs>
                </Box>

                <CustomTabPanel value={value} index={0} className="futures-trade-table futures-trade-table2">
                    {/* ASKS TABLE */}
                    <TableContainer sx={{ maxHeight: 300, scrollbarWidth: 'none', borderRadius: '6px' }}>
                        <Table sx={{ minWidth: 200, overflow: 'clip' }} aria-label="asks-table">
                            <TableHead sx={{ background: "#fff", position: 'sticky', top: 0, zIndex: 1 }}>
                                <TableRow>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {load && !tradePairDetail?.coinTwo ? <Skeleton /> : `Price ${tradePairDetail?.coinTwo ? "(" + tradePairDetail?.coinTwo + ")" : ''}`}
                                    </TableCell>
                                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                                        {load && !tradePairDetail?.coinOne ? <Skeleton /> : `Amount ${tradePairDetail?.coinOne ? "(" + tradePairDetail?.coinOne + ")" : ''}`}
                                    </TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <SkeletonTable row={10} column={3} height={10} />
                                ) : (
                                    asks?.map((row, index) => {
                                        const price = parseFloat(row.price) || 0;
                                        const quantity = parseFloat(row.quantity) || 0;
                                        const total = parseFloat(row.total) || 0;
                                        const percentage = parseFloat(row.percentage) || 0;

                                        return (
                                            <StyledTableRow key={index} sx={{ position: 'relative' }}>
                                                <StyledTableCell
                                                    className="red-txt"
                                                    sx={{ position: 'relative', overflow: 'hidden' }}
                                                >
                                                    {price.toFixed(tradePairDetail?.priceDecimal || 2)}

                                                    <div
                                                        className="prg-bar-red"
                                                        style={{
                                                            width: `${percentage}%`,
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            height: '100%',
                                                            background: 'rgba(255, 0, 0, 0.2)',
                                                            zIndex: 0,
                                                        }}
                                                    />
                                                </StyledTableCell>

                                                <StyledTableCell align="right">
                                                    {(() => {
                                                        const num = parseFloat(quantity || 0);
                                                        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                        return num.toFixed(tradePairDetail?.qtyDecimal || 2);
                                                    })()}
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    {(() => {
                                                        const num = parseFloat(total || 0);
                                                        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                        return num.toFixed(tradePairDetail?.priceDecimal || 2);
                                                    })()}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Current Price Box */}
                    <div className="ord-box">
                        <p className="big-txt grn-txt">
                            {load ? (
                                <Skeleton />
                            ) : tradePairDetail?.lastPrice ? (
                                <>
                                    {parseFloat(tradePairDetail.lastPrice).toFixed(tradePairDetail.priceDecimal)}

                                </>
                            ) : (
                                "-" // fallback when backend fails
                            )}
                        </p>

                    </div>

                    {/* BIDS TABLE */}
                    <TableContainer sx={{ maxHeight: 300, scrollbarWidth: 'none' }}>
                        <Table sx={{ minWidth: 200, overflow: 'clip' }} aria-label="bids-table">
                            <TableHead sx={{ background: "#fff", position: 'sticky', top: 0, zIndex: 1 }}>
                                <TableRow>
                                    <TableCell style={{ whiteSpace: 'nowrap', }}>
                                        {load ? <Skeleton /> : `Price (${tradePairDetail?.coinTwo})`}
                                    </TableCell>
                                    <TableCell align="right" style={{ whiteSpace: 'nowrap', }}>
                                        {load ? <Skeleton /> : `Amount (${tradePairDetail?.coinOne})`}
                                    </TableCell>
                                    <TableCell align="right" style={{ whiteSpace: 'nowrap', }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <SkeletonTable row={10} column={3} height={10} />
                                ) : (
                                    bids?.map((row, index) => {
                                        const price = parseFloat(row.price) || 0;
                                        const quantity = parseFloat(row.quantity) || 0;
                                        const total = parseFloat(row.total) || 0;
                                        const percentage = parseFloat(row.percentage) || 0;

                                        return (
                                            <StyledTableRow key={index} sx={{ position: 'relative' }}>
                                                <StyledTableCell
                                                    className="grn-txt"
                                                    sx={{ position: 'relative', overflow: 'hidden' }}
                                                >
                                                    {price.toFixed(tradePairDetail?.priceDecimal || 2)}

                                                    <div
                                                        className="prg-bar-grn"
                                                        style={{
                                                            width: `${percentage}%`,
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            height: '100%',
                                                            background: 'rgba(0, 255, 0, 0.2)',
                                                            zIndex: 0,
                                                        }}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    {(() => {
                                                        const num = parseFloat(quantity || 0);
                                                        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                        return num.toFixed(tradePairDetail?.qtyDecimal || 2);
                                                    })()}
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    {(() => {
                                                        const num = parseFloat(total || 0);
                                                        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                        return num.toFixed(tradePairDetail?.priceDecimal || 2);
                                                    })()}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>

                <CustomTabPanel value={value} index={1} className="futures-trade-table futures-trade-table2">
                    <TableContainer sx={{ maxHeight: 600, minHeight: '100%', scrollbarWidth: 'none', borderRadius: '6px' }}>


                        <Table sx={{ minWidth: 200, background: "var(--color-BasicBg)", overflow: 'clip' }} stickyHeader aria-label="asks-table">
                            <TableHead sx={{ background: "var(--color-BasicBg)", position: 'sticky', top: 0, zIndex: 1 }}>
                                <TableRow>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>Price ({tradePairDetail?.coinTwo})</TableCell>
                                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>Amount ({tradePairDetail?.coinOne})</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            {/* Current Price */}
                            <div className="ord-box">
                                <p className="big-txt red-txt">
                                    {load ? (
                                        <Skeleton />
                                    ) : tradePairDetail?.lastPrice ? (
                                        <>
                                            {parseFloat(tradePairDetail.lastPrice).toFixed(tradePairDetail.priceDecimal)}

                                        </>
                                    ) : (
                                        "-" // fallback when backend fails
                                    )}
                                </p>

                            </div>
                            <TableBody sx={{ position: 'relative', zIndex: 0 }}>
                                {loading ? (
                                    <SkeletonTable row={10} column={3} height={10} />
                                ) : (
                                    asks?.map((row, index) => (
                                        <StyledTableRow key={index} style={{ position: 'relative' }}>
                                            <StyledTableCell className="red-txt">
                                                {parseFloat(row.price)?.toFixed(tradePairDetail?.priceDecimal)}
                                            </StyledTableCell>

                                            <StyledTableCell align="right">
                                                {(() => {
                                                    const num = parseFloat(row.quantity || 0);
                                                    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                    return num.toFixed(tradePairDetail?.qtyDecimal || 2);
                                                })()}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {(() => {
                                                    const num = parseFloat(row.total || 0);
                                                    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                    return num.toFixed(tradePairDetail?.priceDecimal || 2);
                                                })()}
                                            </StyledTableCell>


                                            {/* Cumulative Depth Bar */}
                                            <div
                                                className="prg-bar-red"
                                                style={{
                                                    width: `${row.percentage || 0}%`,
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    height: '100%',
                                                    background: 'rgba(255, 0, 0, 0.2)',
                                                    zIndex: -1,
                                                }}
                                            />
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>


                </CustomTabPanel>
                <CustomTabPanel value={value} index={2} className="futures-trade-table futures-trade-table2">
                    <TableContainer sx={{ maxHeight: 600, scrollbarWidth: 'none', borderRadius: '6px' }}>
                        <Table sx={{ minWidth: 200, background: "var(--color-BasicBg)", overflow: 'clip' }} stickyHeader aria-label="bids-table">
                            <TableHead sx={{ background: "var(--color-BasicBg)", position: 'sticky', top: 0, zIndex: 1 }}>
                                <TableRow>
                                    <TableCell>Price ({tradePairDetail?.coinTwo})</TableCell>
                                    <TableCell align="right">Amount ({tradePairDetail?.coinOne})</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>

                            {/* Current Price */}
                            <div className="ord-box">
                                <p className="big-txt grn-txt">
                                    {load ? (
                                        <Skeleton />
                                    ) : tradePairDetail?.lastPrice ? (
                                        <>
                                            {parseFloat(tradePairDetail.lastPrice).toFixed(tradePairDetail.priceDecimal)}

                                        </>
                                    ) : (
                                        "-" // fallback when backend fails
                                    )}
                                </p>

                            </div>
                            <TableBody sx={{ position: 'relative', zIndex: 0 }}>
                                {loading ? (
                                    <SkeletonTable row={10} column={3} height={10} />
                                ) : (
                                    bids?.map((row, index) => (
                                        <StyledTableRow key={index} style={{ position: 'relative' }}>
                                            <StyledTableCell className="grn-txt">
                                                {parseFloat(row.price)?.toFixed(tradePairDetail?.priceDecimal)}
                                            </StyledTableCell>


                                            <StyledTableCell align="right">
                                                {(() => {
                                                    const num = parseFloat(row.quantity || 0);
                                                    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                    return num.toFixed(tradePairDetail?.qtyDecimal || 2);
                                                })()}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {(() => {
                                                    const num = parseFloat(row.total || 0);
                                                    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
                                                    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
                                                    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
                                                    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
                                                    return num.toFixed(tradePairDetail?.priceDecimal || 2);
                                                })()}
                                            </StyledTableCell>

                                            {/* Cumulative Depth Bar */}
                                            <div
                                                className="prg-bar-grn"
                                                style={{
                                                    width: `${row.percentage || 0}%`,
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    height: '100%',
                                                    background: 'rgba(0, 255, 0, 0.2)',
                                                    zIndex: -1,
                                                }}
                                            />
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CustomTabPanel>
            </Box>
        </div>

    );
}

export default OrderBook;
